import {
    browserLocationFromGeolocationPosition,
    rememberBrowserLocation,
    type SmartGuideBrowserLocation
} from "./SmartGuideBrowserLocation";

export type SmartGuideLocationProfile = "observe-only" | "balanced" | "precise";
type SmartGuideActiveLocationProfile = Exclude<SmartGuideLocationProfile, "observe-only">;

export const balancedLocationOptions: Readonly<PositionOptions> = Object.freeze({
    enableHighAccuracy: false,
    maximumAge: 15_000,
    timeout: 20_000
});

export const preciseLocationOptions: Readonly<PositionOptions> = Object.freeze({
    enableHighAccuracy: true,
    maximumAge: 5_000,
    timeout: 20_000
});

export type SmartGuideLocationBrokerSubscription = {
    id: string;
    profile: SmartGuideLocationProfile;
    onLocation: (location: SmartGuideBrowserLocation) => void;
    onError?: (error: GeolocationPositionError | Error) => void;
};

type RequestFixOptions = {
    maxAgeMs?: number;
    profile?: SmartGuideActiveLocationProfile;
};

export class SmartGuideLocationBroker {
    private readonly subscriptions = new Map<string, SmartGuideLocationBrokerSubscription>();
    private watchId?: number;
    private watchProfile?: SmartGuideActiveLocationProfile;
    private activeGeolocation?: Geolocation;
    private watchGeneration = 0;
    private oneOffRequestSequence = 0;
    private pendingCurrentPosition?: Promise<SmartGuideBrowserLocation>;
    private destroyed = false;
    private currentFix?: SmartGuideBrowserLocation;

    constructor(
        private readonly geolocationSource: Geolocation | (() => Geolocation | undefined) | undefined =
            () => globalThis.navigator?.geolocation,
        private readonly documentRef: Document | undefined = globalThis.document
    ) {
        this.documentRef?.addEventListener("visibilitychange", this.onVisibilityChanged);
    }

    get latestFix(): SmartGuideBrowserLocation | undefined {
        return this.currentFix;
    }

    subscribe(subscription: SmartGuideLocationBrokerSubscription): () => void {
        if (this.destroyed) {
            throw new Error("The location broker has been destroyed.");
        }

        this.subscriptions.set(subscription.id, subscription);
        if (this.currentFix) {
            subscription.onLocation(this.currentFix);
        }
        this.syncWatch();

        return () => this.unsubscribe(subscription.id);
    }

    unsubscribe(id: string): void {
        if (!this.subscriptions.delete(id)) {
            return;
        }

        this.syncWatch();
    }

    updateProfile(id: string, profile: SmartGuideLocationProfile): void {
        const subscription = this.subscriptions.get(id);
        if (!subscription || subscription.profile === profile) {
            return;
        }

        this.subscriptions.set(id, { ...subscription, profile });
        this.syncWatch();
    }

    requestFix(id: string, options: RequestFixOptions = {}): Promise<SmartGuideBrowserLocation> {
        if (this.unsupportedBrowser) {
            return Promise.reject(new Error("Location is unavailable in this browser."));
        }

        const maxAgeMs = options.maxAgeMs ?? 0;
        if (this.currentFix && Date.now() - this.currentFix.timestamp <= maxAgeMs) {
            return Promise.resolve(this.currentFix);
        }

        if (!this.geolocation) {
            return Promise.reject(new Error("Browser geolocation is unavailable."));
        }

        if (this.watchId === undefined) {
            if (this.pendingCurrentPosition) {
                return this.pendingCurrentPosition;
            }

            const geolocation = this.geolocation;
            const profile = options.profile ?? "balanced";
            const request = new Promise<SmartGuideBrowserLocation>((resolve, reject) => {
                geolocation.getCurrentPosition(
                    position => {
                        const location = this.publishPosition(position);
                        if (!location) {
                            reject(new Error("Browser geolocation returned an invalid position."));
                            return;
                        }

                        resolve(location);
                    },
                    reject,
                    profile === "precise" ? preciseLocationOptions : balancedLocationOptions
                );
            });
            this.pendingCurrentPosition = request;
            const clearPending = (): void => {
                if (this.pendingCurrentPosition === request) {
                    this.pendingCurrentPosition = undefined;
                    this.syncWatch();
                }
            };
            request.then(clearPending, clearPending);
            return request;
        }

        const subscriptionId = `${id}:once:${++this.oneOffRequestSequence}`;
        const replayedFix = this.currentFix;
        return new Promise<SmartGuideBrowserLocation>((resolve, reject) => {
            let unsubscribe = (): void => undefined;
            unsubscribe = this.subscribe({
                id: subscriptionId,
                profile: options.profile ?? "balanced",
                onLocation: location => {
                    if (location === replayedFix) {
                        return;
                    }
                    unsubscribe();
                    resolve(location);
                },
                onError: error => {
                    unsubscribe();
                    reject(error);
                }
            });
        });
    }

    destroy(): void {
        if (this.destroyed) {
            return;
        }

        this.destroyed = true;
        this.documentRef?.removeEventListener("visibilitychange", this.onVisibilityChanged);
        this.subscriptions.clear();
        this.stopWatch();
    }

    private readonly onVisibilityChanged = (): void => {
        this.syncWatch();
    };

    private syncWatch(): void {
        if (this.unsupportedBrowser) {
            this.stopWatch();
            return;
        }

        const desiredProfile = this.desiredProfile();
        const geolocation = this.geolocation;
        if (!desiredProfile || !geolocation || !this.isVisible()) {
            this.stopWatch();
            if (desiredProfile && !geolocation) {
                const error = new Error("Browser geolocation is unavailable.");
                for (const subscription of this.subscriptions.values()) {
                    subscription.onError?.(error);
                }
            }
            return;
        }

        if (this.pendingCurrentPosition) {
            this.stopWatch();
            return;
        }

        if (this.watchId !== undefined && this.watchProfile === desiredProfile) {
            return;
        }

        this.stopWatch();
        const generation = ++this.watchGeneration;
        this.watchProfile = desiredProfile;
        this.activeGeolocation = geolocation;
        this.watchId = geolocation.watchPosition(
            position => this.onPosition(generation, position),
            error => this.onError(generation, error),
            desiredProfile === "precise" ? preciseLocationOptions : balancedLocationOptions
        );
    }

    private stopWatch(): void {
        this.watchGeneration++;
        if (this.watchId !== undefined) {
            this.activeGeolocation?.clearWatch(this.watchId);
        }
        this.watchId = undefined;
        this.watchProfile = undefined;
        this.activeGeolocation = undefined;
    }

    private desiredProfile(): SmartGuideActiveLocationProfile | undefined {
        if (this.subscriptions.size === 0) {
            return undefined;
        }

        const profiles = Array.from(this.subscriptions.values()).map(subscription => subscription.profile);
        if (profiles.includes("precise")) {
            return "precise";
        }

        return profiles.includes("balanced") ? "balanced" : undefined;
    }

    private get geolocation(): Geolocation | undefined {
        return typeof this.geolocationSource === "function"
            ? this.geolocationSource()
            : this.geolocationSource;
    }

    private get unsupportedBrowser(): boolean {
        return this.documentRef?.documentElement.hasAttribute("data-smartguide-unsupported-browser") ?? false;
    }

    private isVisible(): boolean {
        return !this.documentRef || this.documentRef.visibilityState === "visible";
    }

    private onPosition(generation: number, position: GeolocationPosition): void {
        if (generation !== this.watchGeneration) {
            return;
        }

        this.publishPosition(position);
    }

    private publishPosition(position: GeolocationPosition): SmartGuideBrowserLocation | undefined {
        const location = browserLocationFromGeolocationPosition(position);
        if (!location) {
            return undefined;
        }

        this.currentFix = location;
        rememberBrowserLocation(location);
        for (const subscription of Array.from(this.subscriptions.values())) {
            subscription.onLocation(location);
        }

        return location;
    }

    private onError(generation: number, error: GeolocationPositionError): void {
        if (generation !== this.watchGeneration) {
            return;
        }

        for (const subscription of Array.from(this.subscriptions.values())) {
            subscription.onError?.(error);
        }
    }
}

export const smartGuideLocationBroker = new SmartGuideLocationBroker();
